import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../context/AdminContext';
import { formatFecha } from '../utils/fecha';

import { API } from '../config/api';
const ESTADOS = ['Pendiente', 'Aprobada', 'Rechazada', 'Vencida'];
const MONEDAS = ['Colones', 'Dólares', 'Euros'];
const FRECUENCIAS = ['mensual', 'trimestral', 'semestral', 'anual', 'semanal', 'quincenal'];

const EMPTY_FORM = {
  proveedor_id: '', numero_proyecto: '', numero_oferta: '',
  fecha_emision: '', fecha_vencimiento: '', plazo_dias: '',
  moneda: 'Dólares', descripcion: '', estado: 'Pendiente',
  es_recurrente: false, frecuencia: '',
  lineas: []
};

// Nombre humano del periodo actual según frecuencia (para mostrar en panel detalle)
const nombrePeriodoActual = (frecuencia) => {
  const hoy = new Date();
  const year = hoy.getFullYear();
  const mesNombre = hoy.toLocaleDateString('es-CR', { month: 'long' });
  switch (frecuencia) {
    case 'mensual':    return `${mesNombre.charAt(0).toUpperCase() + mesNombre.slice(1)} ${year}`;
    case 'trimestral': return `Q${Math.floor(hoy.getMonth() / 3) + 1} ${year}`;
    case 'semestral':  return `H${Math.floor(hoy.getMonth() / 6) + 1} ${year}`;
    case 'anual':      return `Año ${year}`;
    case 'semanal': {
      // ISO week
      const d = new Date(Date.UTC(year, hoy.getMonth(), hoy.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      return `Semana ${week} de ${year}`;
    }
    case 'quincenal':  return `${hoy.getDate() < 16 ? 'Primera' : 'Segunda'} quincena · ${mesNombre} ${year}`;
    default:           return `${mesNombre} ${year}`;
  }
};

const inputStyle = {
  padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '8px',
  fontSize: '13px', outline: 'none', width: '100%',
  boxSizing: 'border-box', fontFamily: 'Segoe UI, sans-serif'
};

// ── Utilidades ───────────────────────────────────────────────────────────────


const formatMonto = (m) =>
  m != null ? Number(m).toLocaleString('es-CR', { minimumFractionDigits: 2 }) : '0,00';

// Cálculos de líneas: monto = precio unitario sin IVA. Total línea = cantidad × monto.
const totalLineaSinIva = (linea) => {
  const cantidad = parseFloat(linea.cantidad);
  const monto    = parseFloat(linea.monto) || 0;
  return (isNaN(cantidad) || cantidad <= 0 ? 1 : cantidad) * monto;
};

const calcularTotales = (lineas) => {
  const subtotal    = (lineas || []).reduce((s, l) => s + totalLineaSinIva(l), 0);
  const baseGravada = (lineas || []).filter(l => !l.exonerado).reduce((s, l) => s + totalLineaSinIva(l), 0);
  const monto_iva   = baseGravada * 0.13;
  return { subtotal, monto_iva, monto_total_con_iva: subtotal + monto_iva };
};

const montoConIva = (linea) => {
  const total = totalLineaSinIva(linea);
  return linea.exonerado ? total : total * 1.13;
};

// Determina si una cotización está vencida según la fecha actual
const estaVencida = (fechaVenc) => {
  if (!fechaVenc) return false;
  return new Date(fechaVenc) < new Date(new Date().toDateString());
};

// Resuelve el estado real: solo pasa a Vencida si el estado guardado es Pendiente
const estadoEfectivo = (o) =>
  estaVencida(o.fecha_vencimiento) && o.estado === 'Pendiente' ? 'Vencida' : o.estado;

const estadoBadge = (estado) => {
  const estilos = {
    'Pendiente': { background: '#fff8e1', color: '#f57f17' },
    'Aprobada':  { background: '#e8f4e4', color: '#2d6a2d' },
    'Rechazada': { background: '#fdecea', color: '#d32f2f' },
    'Vencida':   { background: '#f0f0f0', color: '#666'    }
  };
  const s = estilos[estado] || { background: '#f0f0f0', color: '#666' };
  return (
    <span style={{ ...s, padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '500' }}>
      {estado || '—'}
    </span>
  );
};

// Badge de estado de pago (independiente del estado funcional)
// Incluye estados de cotizaciones de monto cerrado y recurrentes mensuales
const estadoPagoBadge = (estado_pago) => {
  const estilos = {
    // Monto cerrado
    'No facturada':        { background: '#f0f0f0', color: '#666',    label: 'No facturada' },
    'Con saldo pendiente': { background: '#fff8e1', color: '#f57f17', label: 'Saldo pendiente' },
    'Cancelada':           { background: '#e8f4e4', color: '#2d6a2d', label: 'Cancelada' },
    // Recurrentes (mensuales)
    'Al día':              { background: '#e8f4e4', color: '#2d6a2d', label: '🔁 Al día' },
    'Pago pendiente':      { background: '#fff8e1', color: '#f57f17', label: '🔁 Pago pendiente' },
    'Factura pendiente':   { background: '#fdecea', color: '#d32f2f', label: '🔁 Factura pendiente' }
  };
  const s = estilos[estado_pago] || { background: '#f0f0f0', color: '#666', label: '—' };
  return (
    <span style={{ background: s.background, color: s.color, padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '500' }}>
      {s.label}
    </span>
  );
};

const ESTADOS_PAGO           = ['No facturada', 'Con saldo pendiente', 'Cancelada'];
const ESTADOS_PAGO_RECURRENTE = ['Al día', 'Pago pendiente', 'Factura pendiente'];

// ── Modal eliminar ────────────────────────────────────────────────────────────

const ModalEliminar = ({ titulo, descripcion, onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
    <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 4v6M10 13v1" stroke="#A32D2D" strokeWidth="1.8" strokeLinecap="round"/>
            <circle cx="10" cy="10" r="8.5" stroke="#A32D2D" strokeWidth="1.4"/>
          </svg>
        </div>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#012E40' }}>{titulo}</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{descripcion}</div>
        </div>
      </div>
      <p style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', margin: '0 0 20px' }}>
        ¿Estás seguro que querés eliminar este registro de forma permanente? Esta acción <strong>no se puede deshacer</strong>.
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button onClick={onCancel} style={{ padding: '8px 20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
        <button onClick={onConfirm} style={{ padding: '8px 20px', background: '#A32D2D', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Sí, eliminar</button>
      </div>
    </div>
  </div>
);

// ── Componente principal ──────────────────────────────────────────────────────

export default function Cotizaciones() {
  const isAdmin = useAdmin();

  const [lista,          setLista]          = useState([]);
  const [listaArchiv,    setListaArchiv]     = useState([]);
  const [proveedores,    setProveedores]     = useState([]);
  const [filtro,         setFiltro]          = useState('');
  const [filtroEstadoPago, setFiltroEstadoPago] = useState(''); // '' | 'No facturada' | 'Con saldo pendiente' | 'Cancelada'
  const [filtroArchiv,   setFiltroArchiv]    = useState('');
  const [vistaActiva,    setVistaActiva]     = useState('activas'); // 'activas' | 'archivadas'
  const [modal,          setModal]           = useState(false);
  const [detalle,        setDetalle]         = useState(null);
  const [form,           setForm]            = useState(EMPTY_FORM);
  const [editando,       setEditando]        = useState(false);
  const [cargando,       setCargando]        = useState(true);
  const [cargandoArchiv, setCargandoArchiv]  = useState(false);
  const [modalEliminar,  setModalEliminar]   = useState(null);

  useEffect(() => { cargar(); }, []);

  // Cargar activas siempre; archivadas solo cuando el usuario cambia a esa pestaña
  useEffect(() => {
    if (vistaActiva === 'archivadas' && listaArchiv.length === 0) cargarArchivadas();
  }, [vistaActiva]);

  const cargar = async () => {
    setCargando(true);
    try {
      const [of, pr] = await Promise.all([
        axios.get(`${API}/cotizaciones`),
        axios.get(`${API}/proveedores`)
      ]);
      setLista(of.data);
      setProveedores(pr.data);
    } catch (e) { console.error(e); }
    setCargando(false);
  };

  const cargarArchivadas = async () => {
    setCargandoArchiv(true);
    try {
      const res = await axios.get(`${API}/cotizaciones?archivadas=true`);
      setListaArchiv(res.data);
    } catch (e) { console.error(e); }
    setCargandoArchiv(false);
  };

  // ── Filtros ────────────────────────────────────────────────────────────────

  const filtrados = lista.filter(o => {
    const matchTexto = [o.numero_oferta, o.numero_proyecto, o.proveedor_nombre, o.descripcion]
      .some(v => (v || '').toLowerCase().includes(filtro.toLowerCase()));
    const matchPago = !filtroEstadoPago || o.estado_pago === filtroEstadoPago;
    return matchTexto && matchPago;
  });

  const filtradosArchiv = listaArchiv.filter(o =>
    [o.numero_oferta, o.numero_proyecto, o.proveedor_nombre, o.descripcion]
      .some(v => (v || '').toLowerCase().includes(filtroArchiv.toLowerCase()))
  );

  // ── Stats ──────────────────────────────────────────────────────────────────

  const hayRecurrentes   = lista.some(o => o.es_recurrente);
  const hayNoRecurrentes = lista.some(o => !o.es_recurrente);

  const stats = {
    total:      lista.length,
    pendiente:  lista.filter(o => estadoEfectivo(o) === 'Pendiente').length,
    aprobada:   lista.filter(o => estadoEfectivo(o) === 'Aprobada').length,
    vencida:    lista.filter(o => estadoEfectivo(o) === 'Vencida').length,
    // Monto cerrado
    noFacturada:     lista.filter(o => !o.es_recurrente && o.estado_pago === 'No facturada').length,
    conSaldoPend:    lista.filter(o => !o.es_recurrente && o.estado_pago === 'Con saldo pendiente').length,
    cancelada:       lista.filter(o => !o.es_recurrente && o.estado_pago === 'Cancelada').length,
    // Recurrentes
    alDia:           lista.filter(o => o.es_recurrente && o.estado_pago === 'Al día').length,
    pagoPend:        lista.filter(o => o.es_recurrente && o.estado_pago === 'Pago pendiente').length,
    facturaPend:     lista.filter(o => o.es_recurrente && o.estado_pago === 'Factura pendiente').length,
  };

  // ── Formulario ─────────────────────────────────────────────────────────────

  const cerrarModal = () => {
    if (window.confirm('¿Salir sin guardar? Los cambios se perderán.')) setModal(false);
  };

  const abrirNuevo = () => {
    setForm({ ...EMPTY_FORM, lineas: [{ numero_linea: 1, descripcion: '', cantidad: 1, monto: '', exonerado: false }] });
    setEditando(false);
    setModal(true);
  };

  const abrirEditar = async (o) => {
    const res = await axios.get(`${API}/cotizaciones?id=${o.id}`);
    const d = res.data;
    setForm({
      ...d,
      fecha_emision:     d.fecha_emision    ? d.fecha_emision.split('T')[0]    : '',
      fecha_vencimiento: d.fecha_vencimiento ? d.fecha_vencimiento.split('T')[0] : '',
      plazo_dias:        d.plazo_dias || '',
      lineas: (d.lineas || []).map(l => ({ ...l, exonerado: !!l.exonerado }))
    });
    setEditando(true);
    setDetalle(null);
    setModal(true);
  };

  const verDetalle = async (o) => {
    const res = await axios.get(`${API}/cotizaciones?id=${o.id}`);
    setDetalle(res.data);
  };

  // Calcula fecha de vencimiento a partir de fecha_emision + plazo_dias
  const handlePlazo = (plazo) => {
    const nuevaForm = { ...form, plazo_dias: plazo };
    if (form.fecha_emision && plazo) {
      const base = new Date(form.fecha_emision);
      base.setDate(base.getDate() + parseInt(plazo));
      nuevaForm.fecha_vencimiento = base.toISOString().split('T')[0];
    }
    setForm(nuevaForm);
  };

  // Recalcula vencimiento si cambia la fecha de emisión y hay plazo
  const handleFechaEmision = (fecha) => {
    const nuevaForm = { ...form, fecha_emision: fecha };
    if (fecha && form.plazo_dias) {
      const base = new Date(fecha);
      base.setDate(base.getDate() + parseInt(form.plazo_dias));
      nuevaForm.fecha_vencimiento = base.toISOString().split('T')[0];
    }
    setForm(nuevaForm);
  };

  const guardar = async () => {
    try {
      const { monto_iva, monto_total_con_iva } = calcularTotales(form.lineas);
      const lineasExon = form.lineas.filter(l => l.exonerado).length;
      const iva = lineasExon === form.lineas.length && form.lineas.length > 0 ? 0 : 13;
      const payload = { ...form, iva, monto_iva, monto_total_con_iva };
      if (editando) await axios.put(`${API}/cotizaciones?id=${form.id}`, payload);
      else          await axios.post(`${API}/cotizaciones`, payload);
      setModal(false);
      await cargar();
    } catch (e) {
      console.error(e);
      alert('Error al guardar: ' + (e.response?.data?.error || e.message));
    }
  };

  // ── Archivar / Activar ─────────────────────────────────────────────────────

  const archivar = async (o) => {
    if (!window.confirm(`¿Archivar la cotización ${o.numero_oferta}?`)) return;
    try {
      await axios.patch(`${API}/cotizaciones?id=${o.id}`, { activo: false });
      await cargar();
      setListaArchiv([]); // forzar recarga al volver a archivadas
      if (detalle?.id === o.id) setDetalle(null);
    } catch (e) { alert('Error al archivar: ' + (e.response?.data?.error || e.message)); }
  };

  const activar = async (o) => {
    if (!window.confirm(`¿Activar nuevamente la cotización ${o.numero_oferta}?`)) return;
    try {
      await axios.patch(`${API}/cotizaciones?id=${o.id}`, { activo: true });
      setListaArchiv(prev => prev.filter(x => x.id !== o.id));
      await cargar();
    } catch (e) { alert('Error al activar: ' + (e.response?.data?.error || e.message)); }
  };

  // ── Eliminar (hard) ────────────────────────────────────────────────────────

  const confirmarEliminar = async () => {
    if (!modalEliminar) return;
    try {
      await axios.delete(`${API}/cotizaciones?id=${modalEliminar.id}&hard=true`);
      setModalEliminar(null);
      setDetalle(null);
      await cargar();
      if (vistaActiva === 'archivadas') await cargarArchivadas();
    } catch (e) { alert('Error al eliminar: ' + (e.response?.data?.error || e.message)); }
  };

  // ── Líneas ─────────────────────────────────────────────────────────────────

  const agregarLinea = () =>
    setForm({ ...form, lineas: [...form.lineas, { numero_linea: form.lineas.length + 1, descripcion: '', cantidad: 1, monto: '', exonerado: false }] });

  const actualizarLinea = (i, campo, valor) => {
    const lineas = [...form.lineas];
    lineas[i] = { ...lineas[i], [campo]: valor };
    setForm({ ...form, lineas });
  };

  const eliminarLinea = (i) =>
    setForm({ ...form, lineas: form.lineas.filter((_, idx) => idx !== i).map((l, idx) => ({ ...l, numero_linea: idx + 1 })) });

  // ── Render campo genérico ──────────────────────────────────────────────────

  const campo = (label, key, tipo = 'text', opciones = null) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>{label}</label>
      {opciones
        ? <select value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle}>
            <option value=''>Seleccionar...</option>
            {opciones.map(o => <option key={o}>{o}</option>)}
          </select>
        : <input type={tipo} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle} />
      }
    </div>
  );

  // ── Tabla de registros ─────────────────────────────────────────────────────

  const TablaActivas = () => (
    <>
      <div style={{ padding: '0 24px 12px', background: '#f5f6f8' }}>
        <input
          value={filtro} onChange={e => setFiltro(e.target.value)}
          placeholder='Buscar por número de oferta, proyecto, proveedor...'
          style={{ ...inputStyle, padding: '10px 16px', fontSize: '14px', borderRadius: '10px', width: '100%' }}
        />
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px', background: '#f5f6f8' }}>
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #eee', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#012E40' }}>
                {['N° Oferta', 'Proyecto', 'Proveedor', 'Fecha Emisión', 'Plazo', 'Vencimiento', 'Moneda', 'Total', 'Estado', 'Pago', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#fff', whiteSpace: 'nowrap', borderBottom: '2px solid #87B749' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cargando
                ? <tr><td colSpan={11} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>Cargando...</td></tr>
                : filtrados.length === 0
                  ? <tr><td colSpan={11} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>{lista.length === 0 ? 'No hay cotizaciones registradas aún' : 'Sin resultados'}</td></tr>
                  : filtrados.map((o, i) => {
                      const est = estadoEfectivo(o);
                      return (
                        <tr key={o.id}
                          style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}
                          onClick={() => verDetalle(o)}
                          onMouseOver={e => e.currentTarget.style.background = '#f0f7e8'}
                          onMouseOut={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}>
                          <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: '500', color: '#012E40' }}>{o.numero_oferta}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{o.numero_proyecto || '—'}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.proveedor_nombre}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{formatFecha(o.fecha_emision)}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{o.plazo_dias ? `${o.plazo_dias}d` : '—'}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: est === 'Vencida' ? '#d32f2f' : '#555', fontWeight: est === 'Vencida' ? '600' : '400' }}>{formatFecha(o.fecha_vencimiento)}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{o.moneda}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', fontWeight: '600', color: '#012E40' }}>{formatMonto(o.monto_total_con_iva)}</td>
                          <td style={{ padding: '10px 14px' }}>{estadoBadge(est)}</td>
                          <td style={{ padding: '10px 14px' }}>{estadoPagoBadge(o.estado_pago)}</td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            <button onClick={e => { e.stopPropagation(); abrirEditar(o); }} style={{ padding: '4px 12px', background: 'none', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#555' }}>Editar</button>
                            <button onClick={e => { e.stopPropagation(); archivar(o); }} style={{ padding: '4px 12px', background: 'none', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#999' }}>Archivar</button>
                            {isAdmin && (
                              <button onClick={e => { e.stopPropagation(); setModalEliminar(o); }} style={{ padding: '4px 12px', background: 'none', border: '1px solid #fcc', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#A32D2D' }}>Eliminar</button>
                            )}
                          </td>
                        </tr>
                      );
                    })
              }
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const TablaArchivadas = () => (
    <>
      <div style={{ padding: '0 24px 12px', background: '#f5f6f8' }}>
        <input
          value={filtroArchiv} onChange={e => setFiltroArchiv(e.target.value)}
          placeholder='Buscar en archivadas...'
          style={{ ...inputStyle, padding: '10px 16px', fontSize: '14px', borderRadius: '10px', width: '100%' }}
        />
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px', background: '#f5f6f8' }}>
        <div style={{ background: '#fff8e1', border: '1px solid #f0cc6e', borderRadius: '10px', padding: '10px 16px', marginBottom: '12px', fontSize: '13px', color: '#7a5800' }}>
          Estas cotizaciones han sido archivadas. Podés reactivarlas en cualquier momento.
        </div>
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #eee', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#012E40' }}>
                {['N° Oferta', 'Proyecto', 'Proveedor', 'Vencimiento', 'Moneda', 'Total', 'Estado', 'Pago', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#fff', whiteSpace: 'nowrap', borderBottom: '2px solid #87B749' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cargandoArchiv
                ? <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>Cargando...</td></tr>
                : filtradosArchiv.length === 0
                  ? <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>{listaArchiv.length === 0 ? 'No hay cotizaciones archivadas' : 'Sin resultados'}</td></tr>
                  : filtradosArchiv.map((o, i) => {
                      const est = estadoEfectivo(o);
                      return (
                        <tr key={o.id}
                          style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                          onMouseOver={e => e.currentTarget.style.background = '#fffbee'}
                          onMouseOut={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}>
                          <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: '500', color: '#012E40' }}>{o.numero_oferta}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{o.numero_proyecto || '—'}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.proveedor_nombre}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{formatFecha(o.fecha_vencimiento)}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{o.moneda}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', fontWeight: '600', color: '#012E40' }}>{formatMonto(o.monto_total_con_iva)}</td>
                          <td style={{ padding: '10px 14px' }}>{estadoBadge(est)}</td>
                          <td style={{ padding: '10px 14px' }}>{estadoPagoBadge(o.estado_pago)}</td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            <button onClick={() => activar(o)} style={{ padding: '4px 12px', background: 'none', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#2d6a2d' }}>Activar</button>
                            {isAdmin && (
                              <button onClick={() => setModalEliminar(o)} style={{ padding: '4px 12px', background: 'none', border: '1px solid #fcc', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#A32D2D' }}>Eliminar</button>
                            )}
                          </td>
                        </tr>
                      );
                    })
              }
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  // ── Render principal ───────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'Segoe UI, sans-serif' }}>

      {/* Header */}
      <div style={{ background: '#012E40', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1 style={{ color: '#fff', fontSize: '16px', fontWeight: '600', margin: 0 }}>Cotizaciones de Proveedores</h1>
          <span style={{ background: '#87B749', color: '#fff', fontSize: '11px', padding: '2px 10px', borderRadius: '10px', fontWeight: '500' }}>CxP</span>
        </div>
        <button onClick={abrirNuevo} style={{ padding: '8px 18px', background: 'transparent', color: '#87B749', border: '1.5px solid #87B749', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          + Nueva cotización
        </button>
      </div>

      {/* Stats — fila 1: estado funcional */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', padding: '16px 24px 8px', background: '#f5f6f8', flexShrink: 0 }}>
        {[
          { val: stats.total,    lbl: 'Total activas',  color: '#012E40' },
          { val: stats.pendiente, lbl: 'Pendientes',    color: '#f57f17' },
          { val: stats.aprobada,  lbl: 'Aprobadas',     color: '#2d6a2d' },
          { val: stats.vencida,   lbl: 'Vencidas',      color: '#d32f2f' }
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #eee' }}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: s.color }}>{s.val}</div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>{s.lbl}</div>
          </div>
        ))}
      </div>

      {/* Stats — fila 2: estado de pago — monto cerrado (solo si hay no-recurrentes) */}
      {hayNoRecurrentes && (
        <div style={{ padding: '0 24px 0', background: '#f5f6f8', flexShrink: 0 }}>
          <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
            Monto cerrado
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', paddingBottom: '12px' }}>
            {[
              { val: stats.noFacturada,  lbl: 'No facturadas',        color: '#666',    key: 'No facturada' },
              { val: stats.conSaldoPend, lbl: 'Con saldo pendiente',  color: '#f57f17', key: 'Con saldo pendiente' },
              { val: stats.cancelada,    lbl: 'Canceladas (pagadas)', color: '#2d6a2d', key: 'Cancelada' }
            ].map((s, i) => {
              const activo = filtroEstadoPago === s.key;
              return (
                <div key={i}
                  onClick={() => setFiltroEstadoPago(activo ? '' : s.key)}
                  style={{
                    background: '#fff', borderRadius: '12px', padding: '16px 20px',
                    border: activo ? `2px solid ${s.color}` : '1px solid #eee',
                    cursor: 'pointer', transition: 'border 0.15s'
                  }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{s.lbl}</span>
                    {activo && <span style={{ fontSize: '10px', color: s.color, fontWeight: '600' }}>filtrado ✕</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats — fila 3: estado de pago — recurrentes (solo si hay recurrentes) */}
      {hayRecurrentes && (
        <div style={{ padding: '0 24px 0', background: '#f5f6f8', flexShrink: 0 }}>
          <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
            🔁 Recurrentes — periodo actual
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', paddingBottom: '12px' }}>
            {[
              { val: stats.alDia,        lbl: 'Al día',                 color: '#2d6a2d', key: 'Al día' },
              { val: stats.pagoPend,     lbl: 'Pago pendiente',         color: '#f57f17', key: 'Pago pendiente' },
              { val: stats.facturaPend,  lbl: 'Factura pendiente',      color: '#d32f2f', key: 'Factura pendiente' }
            ].map((s, i) => {
              const activo = filtroEstadoPago === s.key;
              return (
                <div key={i}
                  onClick={() => setFiltroEstadoPago(activo ? '' : s.key)}
                  style={{
                    background: '#fff', borderRadius: '12px', padding: '16px 20px',
                    border: activo ? `2px solid ${s.color}` : '1px solid #eee',
                    cursor: 'pointer', transition: 'border 0.15s'
                  }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{s.lbl}</span>
                    {activo && <span style={{ fontSize: '10px', color: s.color, fontWeight: '600' }}>filtrado ✕</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e0e0e0', padding: '0 24px', background: '#f5f6f8', flexShrink: 0 }}>
        {[
          { key: 'activas',    lbl: `Activas (${lista.length})` },
          { key: 'archivadas', lbl: `Archivadas (${listaArchiv.length})` }
        ].map(t => (
          <button key={t.key} onClick={() => setVistaActiva(t.key)} style={{
            padding: '10px 20px', background: 'none', border: 'none',
            borderBottom: vistaActiva === t.key ? '2px solid #012E40' : '2px solid transparent',
            marginBottom: '-2px', fontSize: '13px', fontWeight: vistaActiva === t.key ? '600' : '400',
            color: vistaActiva === t.key ? '#012E40' : '#888', cursor: 'pointer'
          }}>
            {t.lbl}
          </button>
        ))}
      </div>

      {/* Contenido */}
      {vistaActiva === 'activas' ? <TablaActivas /> : <TablaArchivadas />}

      {/* Panel detalle */}
      {detalle && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '720px', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h3 style={{ color: '#012E40', fontSize: '18px', fontWeight: '700', margin: '0 0 6px' }}>Cotización {detalle.numero_oferta}</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {estadoBadge(estadoEfectivo(detalle))}
                  {detalle.estado_pago && estadoPagoBadge(detalle.estado_pago)}
                  {detalle.es_recurrente && (
                    <span style={{ background: '#f0f7e8', color: '#2d6a2d', fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
                      🔁 Recurrente {detalle.frecuencia || 'mensual'}
                    </span>
                  )}
                  <span style={{ background: '#f0f0f0', color: '#555', fontSize: '11px', padding: '2px 8px', borderRadius: '10px' }}>{detalle.proveedor_nombre}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => abrirEditar(detalle)} style={{ padding: '7px 14px', background: 'none', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: '#555' }}>Editar</button>
                <button onClick={() => { archivar(detalle); setDetalle(null); }} style={{ padding: '7px 14px', background: 'none', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: '#999' }}>Archivar</button>
                {isAdmin && <button onClick={() => setModalEliminar(detalle)} style={{ padding: '7px 14px', background: 'none', border: '1px solid #fcc', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: '#A32D2D' }}>Eliminar</button>}
                <button onClick={() => setDetalle(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#aaa' }}>×</button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', marginBottom: '20px', fontSize: '13px' }}>
              {[
                ['Proveedor',    detalle.proveedor_nombre],
                ['N° Proyecto',  detalle.numero_proyecto || '—'],
                ['Fecha emisión', formatFecha(detalle.fecha_emision)],
                ['Plazo',        detalle.plazo_dias ? `${detalle.plazo_dias} días` : '—'],
                ['Vencimiento',  formatFecha(detalle.fecha_vencimiento)],
                ['Moneda',       detalle.moneda],
                ['IVA aplicado', `${detalle.iva || 0}%`],
                ['Monto IVA',    formatMonto(detalle.monto_iva)],
              ].map(([k, v]) => (
                <div key={k}>
                  <span style={{ color: '#888', fontSize: '11px' }}>{k}</span>
                  <div style={{ fontWeight: '500', color: '#012E40', marginTop: '2px' }}>{v}</div>
                </div>
              ))}
              {detalle.descripcion && (
                <div style={{ gridColumn: '1/-1' }}>
                  <span style={{ color: '#888', fontSize: '11px' }}>Descripción</span>
                  <div style={{ fontWeight: '500', color: '#012E40', marginTop: '2px' }}>{detalle.descripcion}</div>
                </div>
              )}
            </div>

            {/* Panel resumen — modo recurrente: periodo actual */}
            {detalle.es_recurrente && detalle.periodo_actual && (() => {
              const p = detalle.periodo_actual;
              const pagoColor = detalle.estado_pago === 'Al día'
                ? { bg: '#e8f4e4', border: '#C0DD97', txt: '#2d6a2d' }
                : detalle.estado_pago === 'Pago pendiente'
                  ? { bg: '#fff8e1', border: '#FAC775', txt: '#f57f17' }
                  : { bg: '#fdecea', border: '#f5bcbc', txt: '#d32f2f' };
              const nombrePeriodo = nombrePeriodoActual(p.frecuencia || detalle.frecuencia || 'mensual');
              const saldoPeriodo = Math.max(0, (parseFloat(p.factura_periodo_total) || 0) - (parseFloat(p.pagado_periodo) || 0));
              return (
                <div style={{ background: pagoColor.bg, border: `1px solid ${pagoColor.border}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', color: pagoColor.txt, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '10px' }}>
                    🔁 Estado del periodo actual — {nombrePeriodo}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', fontSize: '12px' }}>
                    <div>
                      <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Factura del periodo</div>
                      <div style={{ fontWeight: '600', color: '#012E40', fontSize: '14px', marginTop: '2px' }}>
                        {p.hay_factura ? `${formatMonto(p.factura_periodo_total)} ${detalle.moneda}` : '— sin emitir —'}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pagado del periodo</div>
                      <div style={{ fontWeight: '600', color: '#012E40', fontSize: '14px', marginTop: '2px' }}>{formatMonto(p.pagado_periodo)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Saldo del periodo</div>
                      <div style={{ fontWeight: '700', color: pagoColor.txt, fontSize: '15px', marginTop: '2px' }}>{formatMonto(saldoPeriodo)}</div>
                    </div>
                  </div>
                  {detalle.saldo && (
                    <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: `1px dashed ${pagoColor.border}`, fontSize: '11px', color: '#666', display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
                      <span>Acumulado facturado: <strong style={{ color: '#012E40' }}>{formatMonto(detalle.saldo.facturado)}</strong></span>
                      <span>Acumulado pagado: <strong style={{ color: '#012E40' }}>{formatMonto(detalle.saldo.pagado)}</strong></span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Panel resumen — modo monto cerrado (no recurrente) */}
            {!detalle.es_recurrente && detalle.saldo && (() => {
              const s = detalle.saldo;
              const pagoColor = detalle.estado_pago === 'Cancelada'
                ? { bg: '#e8f4e4', border: '#C0DD97', txt: '#2d6a2d' }
                : detalle.estado_pago === 'Con saldo pendiente'
                  ? { bg: '#fff8e1', border: '#FAC775', txt: '#f57f17' }
                  : { bg: '#f5f6f8', border: '#e0e0e0', txt: '#666' };
              // Base sin IVA: factor proporcional calculado desde monto_iva del encabezado.
              // Funciona aun con líneas exoneradas porque monto_iva solo considera las gravadas.
              const montoTotalCI = parseFloat(s.monto_total) || 0;
              const montoIva     = parseFloat(detalle.monto_iva) || 0;
              const montoSI      = Math.max(0, montoTotalCI - montoIva);
              const factorSI     = montoTotalCI > 0 ? montoSI / montoTotalCI : 1;
              const facturadoSI  = (parseFloat(s.facturado)       || 0) * factorSI;
              const pagadoSI     = (parseFloat(s.pagado)          || 0) * factorSI;
              const saldoSI      = (parseFloat(s.saldo_por_pagar) || 0) * factorSI;
              return (
                <>
                  {/* Panel con IVA — base de cálculo histórica */}
                  <div style={{ background: pagoColor.bg, border: `1px solid ${pagoColor.border}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '10px' }}>
                    <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontWeight: '600' }}>Con IVA</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', fontSize: '12px' }}>
                      <div>
                        <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Monto total</div>
                        <div style={{ fontWeight: '600', color: '#012E40', fontSize: '14px', marginTop: '2px' }}>{formatMonto(s.monto_total)} {detalle.moneda}</div>
                      </div>
                      <div>
                        <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Facturado</div>
                        <div style={{ fontWeight: '600', color: '#012E40', fontSize: '14px', marginTop: '2px' }}>{formatMonto(s.facturado)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pagado</div>
                        <div style={{ fontWeight: '600', color: '#012E40', fontSize: '14px', marginTop: '2px' }}>{formatMonto(s.pagado)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Saldo por pagar</div>
                        <div style={{ fontWeight: '700', color: pagoColor.txt, fontSize: '15px', marginTop: '2px' }}>{formatMonto(s.saldo_por_pagar)}</div>
                      </div>
                    </div>
                  </div>
                  {/* Panel sin IVA — base comparable con montos de líneas */}
                  <div style={{ background: '#fff', border: `1px dashed ${pagoColor.border}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
                    <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontWeight: '600' }}>Sin IVA <span style={{ color: '#aaa', fontWeight: '400', textTransform: 'none' }}>(base comparable con monto de líneas)</span></div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', fontSize: '12px' }}>
                      <div>
                        <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Monto total</div>
                        <div style={{ fontWeight: '600', color: '#012E40', fontSize: '14px', marginTop: '2px' }}>{formatMonto(montoSI)} {detalle.moneda}</div>
                      </div>
                      <div>
                        <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Facturado</div>
                        <div style={{ fontWeight: '600', color: '#012E40', fontSize: '14px', marginTop: '2px' }}>{formatMonto(facturadoSI)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pagado</div>
                        <div style={{ fontWeight: '600', color: '#012E40', fontSize: '14px', marginTop: '2px' }}>{formatMonto(pagadoSI)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Saldo por pagar</div>
                        <div style={{ fontWeight: '700', color: pagoColor.txt, fontSize: '15px', marginTop: '2px' }}>{formatMonto(saldoSI)}</div>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}

            {detalle.lineas && detalle.lineas.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    {['#', 'Descripción', 'Cantidad', 'IVA', 'P. Unit.', 'Total c/IVA'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: (h === 'P. Unit.' || h === 'Total c/IVA' || h === 'Cantidad') ? 'right' : 'left', color: '#666', fontWeight: '500' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detalle.lineas.map(l => (
                    <tr key={l.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px 10px', color: '#888' }}>{l.numero_linea}</td>
                      <td style={{ padding: '8px 10px' }}>{l.descripcion}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555' }}>{l.cantidad ?? 1}</td>
                      <td style={{ padding: '8px 10px' }}>
                        {l.exonerado
                          ? <span style={{ background: '#e8f4e4', color: '#2d6a2d', padding: '2px 8px', borderRadius: '8px', fontSize: '11px' }}>Exonerado</span>
                          : <span style={{ background: '#fff8e1', color: '#f57f17', padding: '2px 8px', borderRadius: '8px', fontSize: '11px' }}>13%</span>}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{formatMonto(l.monto)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '500' }}>{formatMonto(montoConIva(l))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #012E40' }}>
                    <td colSpan={4} style={{ padding: '10px', fontWeight: '700', color: '#012E40', textAlign: 'right' }}>Total</td>
                    <td colSpan={2} style={{ padding: '10px', textAlign: 'right', fontWeight: '700', color: '#012E40', fontSize: '15px' }}>
                      {formatMonto(calcularTotales(detalle.lineas).monto_total_con_iva)} {detalle.moneda}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Modal formulario */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '720px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#012E40', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                {editando ? 'Editar cotización' : 'Nueva cotización'}
              </h3>
              <button onClick={cerrarModal} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#aaa' }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>

              {/* Proveedor */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Proveedor *</label>
                <select value={form.proveedor_id || ''} onChange={e => setForm({ ...form, proveedor_id: e.target.value })} style={inputStyle}>
                  <option value=''>Seleccionar proveedor...</option>
                  {proveedores.map(p => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
                </select>
              </div>

              {campo('N° Proyecto (opcional)', 'numero_proyecto')}
              {campo('N° Oferta / Cotización *', 'numero_oferta')}
              {campo('Estado', 'estado', 'text', ESTADOS)}

              {/* Fecha emisión — con recálculo de vencimiento */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Fecha Emisión *</label>
                <input type='date' value={form.fecha_emision || ''} onChange={e => handleFechaEmision(e.target.value)} style={inputStyle} />
              </div>

              {/* Plazo en días */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Plazo (días)</label>
                <input
                  type='number' min='1' value={form.plazo_dias || ''}
                  onChange={e => handlePlazo(e.target.value)}
                  style={inputStyle} placeholder='Ej: 30'
                />
              </div>

              {/* Fecha vencimiento — calculada automáticamente, editable manualmente */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Fecha Vencimiento</label>
                <input
                  type='date' value={form.fecha_vencimiento || ''}
                  onChange={e => setForm({ ...form, fecha_vencimiento: e.target.value })}
                  style={{ ...inputStyle, background: form.plazo_dias ? '#f0f7e8' : '' }}
                />
                {form.plazo_dias && <span style={{ fontSize: '10px', color: '#87B749' }}>Calculada desde fecha de emisión + {form.plazo_dias} días</span>}
              </div>

              {/* Cotización recurrente — junto a plazo/vencimiento */}
              <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 14px', background: form.es_recurrente ? '#f0f7e8' : '#fafafa', border: `1px solid ${form.es_recurrente ? '#C0DD97' : '#eee'}`, borderRadius: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}>
                  <input
                    type='checkbox'
                    checked={!!form.es_recurrente}
                    onChange={e => setForm({ ...form, es_recurrente: e.target.checked, frecuencia: e.target.checked ? (form.frecuencia || 'mensual') : '' })}
                    style={{ width: 16, height: 16, accentColor: '#87B749', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#012E40' }}>
                      🔁 Cotización recurrente
                    </div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                      Servicio repetitivo en un periodo. El saldo se evalúa por periodo corriente (no contra el monto total).
                    </div>
                  </div>
                </label>
                {form.es_recurrente && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '160px' }}>
                    <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Frecuencia *</label>
                    <select
                      value={form.frecuencia || 'mensual'}
                      onChange={e => setForm({ ...form, frecuencia: e.target.value })}
                      style={inputStyle}
                    >
                      {FRECUENCIAS.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {campo('Moneda', 'moneda', 'text', MONEDAS)}
              <div style={{ gridColumn: '1/-1' }}>{campo('Descripción general', 'descripcion')}</div>
            </div>

            {/* Líneas */}
            <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ color: '#012E40', fontSize: '14px', fontWeight: '600', margin: 0 }}>Líneas de la cotización</h4>
                <button onClick={agregarLinea} style={{ padding: '5px 12px', background: '#87B749', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>+ Agregar línea</button>
              </div>
              {form.lineas.length === 0
                ? <p style={{ color: '#aaa', fontSize: '13px', textAlign: 'center', padding: '16px' }}>Sin líneas. Agregá al menos una.</p>
                : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        {['#', 'Descripción', 'Cantidad', 'P. Unit.', 'Exonerado', 'Total c/IVA', ''].map(h => (
                          <th key={h} style={{ padding: '8px', textAlign: (h === 'P. Unit.' || h === 'Total c/IVA' || h === 'Cantidad') ? 'right' : h === 'Exonerado' ? 'center' : 'left', color: '#666', fontWeight: '500' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {form.lineas.map((l, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '6px 8px', color: '#888' }}>{l.numero_linea}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <input value={l.descripcion} onChange={e => actualizarLinea(i, 'descripcion', e.target.value)} style={{ ...inputStyle, padding: '5px 8px' }} placeholder='Descripción del servicio' />
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input type='number' min='0' step='0.01' value={l.cantidad ?? 1} onChange={e => actualizarLinea(i, 'cantidad', e.target.value)} style={{ ...inputStyle, padding: '5px 8px', textAlign: 'right', width: 80 }} placeholder='1' />
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input type='number' value={l.monto} onChange={e => actualizarLinea(i, 'monto', e.target.value)} style={{ ...inputStyle, padding: '5px 8px', textAlign: 'right', width: 110 }} placeholder='0.00' />
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <label style={{ position: 'relative', display: 'inline-block', width: '36px', height: '20px', cursor: 'pointer' }}>
                              <input type='checkbox' checked={!!l.exonerado} onChange={e => actualizarLinea(i, 'exonerado', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                              <span style={{ position: 'absolute', inset: 0, background: l.exonerado ? '#87B749' : '#ddd', borderRadius: '20px', transition: '.2s' }}>
                                <span style={{ position: 'absolute', height: '14px', width: '14px', left: l.exonerado ? '19px' : '3px', bottom: '3px', background: '#fff', borderRadius: '50%', transition: '.2s' }} />
                              </span>
                            </label>
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '500', color: l.exonerado ? '#2d6a2d' : '#012E40' }}>{formatMonto(montoConIva(l))}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <button onClick={() => eliminarLinea(i)} style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '16px' }}>×</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #012E40' }}>
                        <td colSpan={5} style={{ padding: '10px 8px', fontWeight: '700', color: '#012E40', textAlign: 'right' }}>Total</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', color: '#012E40', fontSize: '15px' }}>
                          {formatMonto(calcularTotales(form.lineas).monto_total_con_iva)} {form.moneda}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
              }
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
              <button onClick={cerrarModal} style={{ padding: '8px 20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
              <button onClick={guardar} style={{ padding: '8px 20px', background: '#87B749', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                {editando ? 'Guardar cambios' : 'Crear cotización'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal eliminar */}
      {modalEliminar && (
        <ModalEliminar
          titulo="Eliminar cotización"
          descripcion={`${modalEliminar.numero_oferta} — ${modalEliminar.proveedor_nombre}`}
          onConfirm={confirmarEliminar}
          onCancel={() => setModalEliminar(null)}
        />
      )}
    </div>
  );
}
